export interface NameableMember {
  userId: string
  name?: string
  email?: string
}

/**
 * How to refer to each member **in a sentence addressed to the viewer**.
 *
 * A nameless profile must not surface its whole address mid-sentence — the local part reads
 * as a name, the domain is noise.
 *
 * `viewerId` is opt-in to the second person. Pass it and that member becomes `you`; omit it
 * and every member is named in the third person.
 *
 * **`you` belongs in sentences, never in labels.** The `Waiting on` line is prose addressed to
 * the viewer, so `Waiting on you, Bo` is right — and the viewer stays in the list, because
 * `Waiting on you` on your own unanswered poll is a useful nudge rather than an oversight. A
 * table row header is a label in a third-person grid: `you` sitting in a column of names would
 * read as a different kind of thing from its neighbours, and the matrix is a reading surface
 * where consistency of form *is* the affordance. **Row headers must call this without
 * `viewerId`.**
 */
export const buildMemberNames = (members: NameableMember[], viewerId?: string): Record<string, string> =>
  Object.fromEntries(
    members.map(member => [
      member.userId,
      member.userId === viewerId ? 'you' : member.name || member.email?.split('@')[0] || member.userId,
    ])
  )
